// ============================================================================
// dataTypes/liveStreamManagment/audioPipeline/loudness.ts
//
// Loudness ITU-R BS.1770 / EBU R128 (2026-10-23) pour les VU-mètres de la régie
// en mode LUFS.
//
//   1. Pondération K : deux biquads en série — un plateau haut (+4 dB au-dessus
//      de ~1,7 kHz, la tête) puis un passe-haut RLB (~38 Hz). Coefficients
//      recalculés pour la fréquence d'échantillonnage du contexte (formules de
//      libebur128), posés dans des IIRFilterNode par AudioSourceRegistry.
//   2. Moyenne quadratique du signal pondéré sur 400 ms (LUFS « Momentary »).
//   3. L = −0,691 + 10·log10(Σ canaux), poids 1 pour L et R.
//
// Référence EBU Tech 3341 : un sinus stéréo 1 kHz de crête −23 dBFS se lit
// −23,0 LUFS (cf. loudness.test.ts).
// ============================================================================

export interface BiquadCoefficients {
  /** Numérateur [b0, b1, b2]. */
  feedforward: number[];
  /** Dénominateur [1, a1, a2]. */
  feedback:    number[];
}

/** Fenêtre du LUFS Momentary. */
export const MOMENTARY_WINDOW_S = 0.4;

/** Plancher d'affichage : en dessous, le silence. */
export const LUFS_FLOOR = -60;

/** Les deux étages de la pondération K pour une fréquence d'échantillonnage. */
export function kWeightingCoefficients(sampleRate: number): [BiquadCoefficients, BiquadCoefficients] {
  // Étage 1 — plateau haut (effet acoustique de la tête).
  const f0 = 1681.974450955533;
  const G  = 3.999843853973347;
  const Q  = 0.7071752369554196;
  const K  = Math.tan((Math.PI * f0) / sampleRate);
  const Vh = Math.pow(10, G / 20);
  const Vb = Math.pow(Vh, 0.4996667741545416);
  const a0 = 1 + K / Q + K * K;
  const shelf: BiquadCoefficients = {
    feedforward: [(Vh + (Vb * K) / Q + K * K) / a0, (2 * (K * K - Vh)) / a0, (Vh - (Vb * K) / Q + K * K) / a0],
    feedback:    [1, (2 * (K * K - 1)) / a0, (1 - K / Q + K * K) / a0],
  };

  // Étage 2 — passe-haut RLB (« revised low-frequency B-weighting »).
  const f1 = 38.13547087602444;
  const Q1 = 0.5003270373238773;
  const K1 = Math.tan((Math.PI * f1) / sampleRate);
  const d  = 1 + K1 / Q1 + K1 * K1;
  const highpass: BiquadCoefficients = {
    feedforward: [1, -2, 1],
    feedback:    [1, (2 * (K1 * K1 - 1)) / d, (1 - K1 / Q1 + K1 * K1) / d],
  };

  return [shelf, highpass];
}

/** Moyenne quadratique des `count` derniers échantillons d'un tampon. */
export function meanSquareTail(data: Float32Array, count: number): number {
  const n     = Math.max(1, Math.min(count, data.length));
  const start = data.length - n;
  let sum = 0;
  for (let i = start; i < data.length; i++) sum += data[i] * data[i];
  return sum / n;
}

/** Loudness (LUFS) à partir des moyennes quadratiques pondérées K de chaque canal. */
export function lufsFromMeanSquares(meanSquares: number[], floor = LUFS_FLOOR): number {
  const sum = meanSquares.reduce((a, b) => a + b, 0);
  if (sum <= 0) return floor;
  return Math.max(floor, -0.691 + 10 * Math.log10(sum));
}
